/**
 * CompanyCurrencyEditor — Self-service currency switcher for company
 * admins. Replaces the old "Contact your administrator" text on the
 * System Settings → Currency card.
 *
 * UX:
 *   • Loads the whitelisted ISO codes from
 *     GET /api/companies/me/currency/options on mount.
 *   • Renders a Select. Selecting a different value enables a Save
 *     button (no autosave — currency changes are consequential).
 *   • Save confirms with the user before flipping ("This affects only
 *     future orders. Existing orders keep their original currency.").
 *   • Calls PUT /api/companies/me/currency on confirm.
 *   • Non-admins see the legacy read-only display; the parent already
 *     decides whether to render us, but we also defend against being
 *     rendered for a non-admin by reading is_admin from the AuthContext.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CompanyCurrencyEditor({ currentCurrency, onSaved }) {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || (user?.roles || []).includes('admin'));

  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(currentCurrency || 'AED');
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(currentCurrency || 'AED');
  }, [currentCurrency]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/companies/me/currency/options`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setOptions(res.data?.currencies || []);
      } catch (e) {
        // Fall back to a sane default list so the UI still works if
        // the endpoint is briefly unreachable.
        if (!cancelled) {
          setOptions([
            { code: 'AED', name: 'UAE Dirham' },
            { code: 'INR', name: 'Indian Rupee' },
            { code: 'USD', name: 'US Dollar' },
            { code: 'GBP', name: 'Pound Sterling' },
            { code: 'EUR', name: 'Euro' },
            { code: 'SAR', name: 'Saudi Riyal' },
          ]);
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const changed = selected && selected !== (currentCurrency || 'AED');

  // Non-admin fallback — read-only display.
  if (!isAdmin) {
    return (
      <>
        <p className="text-wt-text-secondary text-sm">
          Your company's currency is set by your administrator.
        </p>
        <div className="p-3 rounded-lg bg-wt-bg border border-wt-border" data-testid="company-currency-readonly">
          <p className="text-wt-text-primary font-medium">
            Current: {currentCurrency || 'AED'}
          </p>
        </div>
        <p className="text-xs text-wt-text-muted">
          Contact your company admin to change currency settings.
        </p>
      </>
    );
  }

  const handleSave = async () => {
    if (!changed) return;
    const confirmed = window.confirm(
      `Change your company currency from ${currentCurrency} to ${selected}?\n\n` +
      `This affects only NEW orders, displays, and incentive calculations. ` +
      `Existing orders and ledger entries keep their original currency.`
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/companies/me/currency`,
        { currency: selected },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      if (onSaved) onSaved(res.data?.currency || selected);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Save failed';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="company-currency-editor">
      <p className="text-xs text-wt-text-muted">
        Choose the base currency for your company. Affects new orders,
        displays, and incentive calculations.
      </p>

      <Select value={selected} onValueChange={setSelected} disabled={loadingOptions || saving}>
        <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary"
                       data-testid="company-currency-select">
          <SelectValue placeholder={loadingOptions ? 'Loading currencies…' : 'Select a currency'} />
        </SelectTrigger>
        <SelectContent className="bg-wt-paper border-wt-border">
          {options.map((c) => (
            <SelectItem
              key={c.code}
              value={c.code}
              data-testid={`company-currency-option-${c.code}`}
            >
              <span className="font-mono text-wt-gold mr-2">{c.code}</span>
              <span className="text-wt-text-muted">{c.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-wt-text-muted">
          Current: <span className="font-mono text-wt-text-primary">{currentCurrency || 'AED'}</span>
        </p>
        <Button
          onClick={handleSave}
          disabled={!changed || saving}
          className="bg-wt-gold hover:bg-wt-gold/90 text-white"
          data-testid="company-currency-save-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {changed && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5"
             data-testid="company-currency-change-notice">
          <Info className="h-3 w-3 mt-0.5 flex-none" />
          <span>
            Only NEW records use the new currency. Existing orders, ledger
            entries, and incentive payouts keep their original currency.
          </span>
        </div>
      )}
    </div>
  );
}
